import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import QRCode from 'qrcode';
import { useAuth } from 'react-oidc-context';
import { translate as t } from 'hooks/useTranslations';
import { AppContext, storeLastUrl } from '../../context';
import _ from 'lodash';
import { getEnv } from 'data_sources/constants';
import useStepUp from '../LexID/Organizations/_data_sources/useStepUp';
import { useLocation } from 'react-router-dom';
import CommonLayout from '../_layouts/CommonLayout';

export default function MFASettings() {
  const currLoc = useLocation();
  const auth = useAuth();
  const stepUp = useStepUp();
  const [secretCode, setSecretCode] = useState('');
  const [qrCodeString, setQrCodeString] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [tokenVerified, setTokenVerified] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!auth.user?.scopes?.includes('aws.cognito.signin.user.admin')) {
      // Check if we've attempted a redirect in the last 5 minutes
      const redirectAttempted = document.cookie.split('; ').find((row) => row.startsWith('phx-redirect-attempt='));

      if (!redirectAttempted) {
        // Set cookie that expires in 5 minutes
        const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString();
        document.cookie = `phx-redirect-attempt=true; expires=${expires}; path=/`;

        // Use the helper function to safely store the URL
        storeLastUrl(currLoc);

        auth.signinRedirect({
          scope: ['openid', 'profile', 'email', 'phone', 'aws.cognito.signin.user.admin'].join(' '),
        });
      }
    } else {
      // Delete the cookie if user has the required scope
      document.cookie = 'phx-redirect-attempt=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setSetupError(t('organizations.stepUp.notAuthenticated'));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setSetupError('');

    stepUp.associateSoftwareToken(
      (response) => {
        setSecretCode(response.secretCode);
        setIsLoading(false);
      },
      (errors) => {
        console.error('Software token error:', errors);
        setSetupError(t('organizations.stepUp.setupFailed'));
        setIsLoading(false);
      }
    );
  }, [auth.isAuthenticated, t]);

  useEffect(() => {
    if (auth.user && !_.isEmpty(secretCode)) {
      const env = getEnv();
      const issuer = env === 'prod' ? 'Lexipol' : `Lexipol (${env.toUpperCase()})`;
      const username = auth.user.profile['cognito:username'] || auth.user.profile.sub;

      QRCode.toDataURL(
        `otpauth://totp/${encodeURIComponent(issuer)}:${username}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`
      ).then((str) => setQrCodeString(str));
    } else {
      setQrCodeString('');
    }
  }, [auth.user, secretCode]);

  const handleTotpVerify = async () => {
    if (!auth.isAuthenticated) {
      setTokenError(true);
      setTokenVerified(false);
      return;
    }

    setTokenError(false);
    setTokenVerified(false);

    try {
      const handleVerificationSuccess = () => {
        setTokenVerified(true);
        setTokenError(false);
      };

      const handleVerificationError = () => {
        setTokenVerified(false);
        setTokenError(true);
      };

      await stepUp.validateSoftwareToken(
        totpCode,
        (response) => {
          if (response.status?.toUpperCase() === 'SUCCESS') {
            handleVerificationSuccess();
          } else {
            handleVerificationError();
          }
        },
        (errors) => {
          console.error('TOTP verification failed', errors);
          handleVerificationError();
        }
      );
    } catch (error) {
      console.error('TOTP verification failed', error);
      setTokenVerified(false);
      setTokenError(true);
    }
  };

  return (
    <CommonLayout
      headerProps={{
        title: t('mfaSettings.title'),
      }}
    >
      <Box
        className='mfa-settings'
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 200px)', // Adjust for header and footer
        }}
      >
        <Card sx={{ width: 800, maxWidth: '100%', p: 6 }}>
          <CardContent>
            <Stack spacing={5}>
              <Stack
                spacing={2}
                sx={{ alignItems: 'center' }}
              >
                <img
                  src='/admin/img/lexipol_logo.svg'
                  alt='Lexipol'
                  style={{ display: 'inline-block', width: 48 }}
                />
                <Typography variant='h4'>{t('mfaSettings.title')}</Typography>
                {isLoading ? (
                  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {setupError && (
                      <Box sx={{ p: 3 }}>
                        <Alert severity='error'>{setupError}</Alert>
                      </Box>
                    )}
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Stack spacing={3}>
                        <Typography variant='h6'>{t('organizations.stepUp.setupMFA')}</Typography>
                        {!secretCode ? (
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            {isLoading ? t('organizations.stepUp.loading') : t('organizations.stepUp.setupMFA')}
                          </Typography>
                        ) : (
                          <>
                            {!_.isEmpty(qrCodeString) && (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <img
                                  src={qrCodeString}
                                  alt='QR Code for auth app'
                                />
                              </Box>
                            )}
                            <Typography
                              variant='body2'
                              color='text.secondary'
                            >
                              {t('organizations.stepUp.setupQRCode')}
                            </Typography>
                            <Stack
                              direction='row'
                              spacing={2}
                              alignItems='flex-start'
                              justifyContent='center'
                            >
                              <TextField
                                size='small'
                                label={false}
                                value={totpCode}
                                onChange={(e) => setTotpCode(e.target.value)}
                                error={!!tokenError}
                                helperText={
                                  tokenError
                                    ? t('organizations.stepUp.tokenVerificationError')
                                    : t('organizations.stepUp.otpCodeLabel')
                                }
                                sx={{
                                  '&.MuiFormControl-root': {
                                    paddingTop: 0,
                                  },
                                }}
                              />
                              <Button
                                variant='contained'
                                onClick={handleTotpVerify}
                                disabled={!totpCode || !auth.isAuthenticated}
                                size='small'
                                sx={{ mt: 0.5 }}
                              >
                                {t('organizations.stepUp.tokenVerification')}
                              </Button>
                            </Stack>
                            {tokenVerified && (
                              <Alert severity='success'>{t('organizations.stepUp.tokenVerifiedSuccess')}</Alert>
                            )}
                          </>
                        )}
                      </Stack>
                    </Box>
                  </>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </CommonLayout>
  );
}
